import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import Layout, { DocuLayout } from '../components/Layout';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DocuLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1>{`How to connect Wingbot Webchat to any website`}</h1>
    <h2>{`Basic setup`}</h2>
    <p>{`On the example below you can see the minimal setup for connecting the Wingbot webchat to a website.`}</p>
    <deckgo-highlight-code {...{
      "html": "",
      "line-numbers": "true",
      "highlight-lines": ""
    }}>{`
          `}<code parentName="deckgo-highlight-code" {...{
        "slot": "code"
      }}>{`<link href="https://orchestrator.wingbot.ai/styles/wingbotChat.css" rel='stylesheet' type='text/css'>
<div id="chat"></div>
<script>
    window.wingbotChatCmds=window.wingbotChatCmds||[];
    window.wingbotChat=window.wingbotChat||function(){wingbotChatCmds.push(arguments)}

    wingbotChat('init', {
        pageId: '<put your page-id here>',
        contextUserAgentProp: 'ua',
		contextScreenResolutionProp: 'sr'
    }, document.getElementById('chat'));
</script>
<script async src="https://orchestrator.wingbot.ai/wingbotChat.js"></script>`}</code>{`
        `}</deckgo-highlight-code>
    <br />
    <br />
    <h5>{`We can split this example code to the 4 main parts:`}</h5>
    <ul>
      <li parentName="ul"><strong parentName="li">{`CSS styles`}</strong>
        <ul parentName="li">
          <li parentName="ul">{`link with the basic webchat theme, working out of the box`}</li>
          <li parentName="ul">{`feel free to create your own and replace it`}</li>
        </ul>
      </li>
      <li parentName="ul"><strong parentName="li">{`Physical representation`}</strong>
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`<div id="chat"></div>`}</inlineCode>{` is physical representation of the webchat`}</li>
          <li parentName="ul">{`it can be placed anywhere in your website`}</li>
        </ul>
      </li>
      <li parentName="ul"><strong parentName="li">{`Webchat configuration`}</strong>
        <ul parentName="li">
          <li parentName="ul">{`this part is mostly for configuration the webchat configuration`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`pageId`}</inlineCode>{` is mandatory and unique, if you don't know your `}<inlineCode parentName="li">{`pageId`}</inlineCode>{` feel free to contact Wingbot support`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`document.getElementById`}</inlineCode>{` can be set to any existing element in the website`}</li>
        </ul>
      </li>
      <li parentName="ul"><strong parentName="li">{`Source to the webchat code`}</strong>
        <ul parentName="li">
          <li parentName="ul">{`the last line of the example is link to the actual webchat app`}</li>
          <li parentName="ul">{`please do not download it and/or cache it somewhere in your infrastructure if you want to have the latest and the most secure version of Wingbot webchat`}</li>
        </ul>
      </li>
    </ul>
    <h2>{`Dig more deeply into the configuration`}</h2>
    <p>{`As we saw in the example above, webchat is configurable with 3 parameters.`}</p>
    <p>{`Lets have a look little bit deeply about the configuration right now:`}</p>
    <h3>{`Webchat commands and parameters:`}</h3>
    <deckgo-highlight-code {...{
      "javascript": "",
      "line-numbers": "true",
      "highlight-lines": ""
    }}>{`
          `}<code parentName="deckgo-highlight-code" {...{
        "slot": "code"
      }}>{`webchat (
    'init',    // \`'init'\` command should be always called to start a chat
    element,   // is for the DOM element which is representation of the chat on the page
    config     // object with webchat configurations
);
`}</code>{`
        `}</deckgo-highlight-code>
    <br />
    <br />
    <p>{`There are supported parameters for the config object:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Parameter`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Default value`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Example`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`pageId`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`required`}</strong></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Identifier of page`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`'134271f0-1d1c-42a3-9191-6288fd1a263b'`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`api`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`null`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Override an API URL of orchestrator (on-premise solutions only)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`websocket`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`null`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Override an websocket URL of the widget (on-premise solutions only)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`token`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`null`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Conversation token to continue in already existing conversation`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`refreshInterval`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`15000`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Interval in miliseconds to refresh the credentials`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`startAction`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`start`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Postback payload to send a non-visual chat event for start the conversation`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`lang`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`null`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Force the widget to use specified language`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`fallbackLang`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`'en'`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Use this language if it cannot be detected automatically`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`reduxEnhancer`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`null`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Redux plugin to be used within chat`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`onInit`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`() => {}`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Will be called when chat has been successfuly initialized`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`headerText`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`null`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Set a static text to be used as a header`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`context`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`{}`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Thread context variables - will be pushed to the bot`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`uploadEnabled`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`On/off for a file upload (boolean)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`uploadSizeLimit`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`1048576 * 6`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Hard limit for the file upload, bigger file cannot be uploaded`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`uploadSoftLimitForResize`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`1048576 * 2.5`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Soft limit for the file upload, bigger file will be shrunk, `}<inlineCode parentName="td">{`0`}</inlineCode>{` means no shrinking`}</td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`contextUserAgentProp`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`null`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Context prop to store user agent`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`'ua'`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`contextScreenResolutionProp`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`null`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Context prop to store screen resolution`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`'sr'`}</inlineCode></td>
        </tr>
      </tbody>
    </table>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      